import { useEffect, useMemo, useState } from 'react'
import { ArchiveScene } from './components/ArchiveScene'
import { Cursor } from './components/Cursor'
import { ArrowIcon, AsteriskIcon, SoundIcon } from './components/Icons'
import { SceneBoundary } from './components/SceneBoundary'
import { useAmbientSound } from './hooks/useAmbientSound'
import { useExperienceScroll } from './hooks/useExperienceScroll'
import { echoColors, experienceState, type EchoChoice } from './experienceState'

const chapters = [
  { id: 'threshold', label: 'Threshold' },
  { id: 'turn', label: 'The Turn' },
  { id: 'wild', label: 'The Wild' },
  { id: 'still', label: 'The Still' },
  { id: 'echo', label: 'Your Echo' },
  { id: 'coda', label: 'Coda' },
]

function Loader({ progress, exiting, onEnter, onEnterSilent }: {
  progress: number
  exiting: boolean
  onEnter: () => void
  onEnterSilent: () => void
}) {
  const ready = progress >= 100
  return (
    <div className={`loader ${ready ? 'is-ready' : ''} ${exiting ? 'is-exiting' : ''}`} role="dialog" aria-label="Enter the experience">
      <div className="loader-noise" aria-hidden="true" />
      <div className="loader-topline">
        <span>ALMOST / ARCHIVE</span>
        <span>WEBGL–26</span>
      </div>

      <div className="loader-orbit" aria-hidden="true">
        <div className="loader-orbit-ring" />
        <AsteriskIcon />
      </div>

      <div className="loader-center">
        <p className="eyebrow">A browser-based monument to paths not taken</p>
        <div className="loader-word" aria-label="Almost">
          <span>ALM</span><i>·</i><span>OST</span>
        </div>
        <p className="loader-whisper">Please arrive as the person you nearly became.</p>
      </div>

      <div className="loader-bottom">
        <div className="load-status" aria-live="polite">
          <span>{ready ? 'THE DOOR IS OPEN' : 'ASSEMBLING MEMORIES'}</span>
          <strong>{String(Math.round(progress)).padStart(3, '0')}%</strong>
        </div>
        <div className="load-track"><span style={{ transform: `scaleX(${progress / 100})` }} /></div>
        <div className="entry-actions">
          <button className="enter-button" type="button" onClick={onEnter} disabled={!ready}>
            <span>Enter the archive</span><ArrowIcon />
          </button>
          <button className="silent-button" type="button" onClick={onEnterSilent} disabled={!ready}>
            Enter without sound
          </button>
        </div>
      </div>
    </div>
  )
}

function Header({ chapter, soundActive, toggleSound }: {
  chapter: number
  soundActive: boolean
  toggleSound: () => void
}) {
  const jumpTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  return (
    <header className="site-header">
      <button className="brand" type="button" onClick={() => jumpTo('threshold')} aria-label="Back to the beginning">
        <span className="brand-mark"><AsteriskIcon /></span>
        <span>ALMOST</span>
      </button>

      <nav className="chapter-nav" aria-label="Experience chapters">
        {chapters.map((item, index) => (
          <button
            type="button"
            key={item.id}
            className={chapter === index ? 'is-active' : ''}
            onClick={() => jumpTo(item.id)}
            aria-label={`Go to ${item.label}`}
            aria-current={chapter === index ? 'step' : undefined}
          >
            <span />
            <em>{item.label}</em>
          </button>
        ))}
      </nav>

      <div className="header-meta">
        <span className="chapter-readout">{String(chapter + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')}</span>
        <button className="sound-toggle" type="button" onClick={toggleSound} aria-label={soundActive ? 'Mute ambient sound' : 'Play ambient sound'}>
          <SoundIcon active={soundActive} />
          <span>{soundActive ? 'Sound on' : 'Sound off'}</span>
        </button>
      </div>
    </header>
  )
}

function ChapterMeta({ code, time, children }: { code: string; time: string; children: React.ReactNode }) {
  return (
    <div className="chapter-meta">
      <span>{code}</span><span>{time}</span><span>{children}</span>
    </div>
  )
}

function Story({ choice, setChoice, released, releaseEcho, chime }: {
  choice: EchoChoice
  setChoice: (value: EchoChoice) => void
  released: boolean
  releaseEcho: () => void
  chime: (frequency?: number) => void
}) {
  const choose = (value: EchoChoice, note: number) => {
    setChoice(value)
    experienceState.choice = value
    experienceState.releasePulse = 0.42
    chime(note)
  }

  return (
    <main id="main-content" className="story">
      <section id="threshold" className="hero-section" aria-labelledby="hero-title">
        <div className="hero-word" id="hero-title"><span>ALM</span><span>OST</span></div>
        <div className="hero-copy">
          <p className="eyebrow reveal-line">The Museum of Unlived Lives</p>
          <h1>Every life you didn’t live<br /><em>is still dreaming somewhere.</em></h1>
          <p className="hero-intro">An interactive monument to the choices, accidents, and quiet little turns that make one life yours.</p>
          <button className="scroll-prompt" type="button" onClick={() => document.getElementById('turn')?.scrollIntoView({ behavior: 'smooth' })}>
            <span className="scroll-glyph"><i /></span>
            <span>Scroll to cross the threshold</span>
          </button>
        </div>
        <div className="hero-coordinates" aria-hidden="true">
          <span>17° 24′ 00″ N</span>
          <span>78° 29′ 00″ E</span>
        </div>
        <p className="hero-side-note">ONE BODY<br />INFINITE DRAFTS</p>
      </section>

      <section id="turn" className="chapter chapter-right" aria-labelledby="turn-title">
        <div className="chapter-card">
          <ChapterMeta code="ARCHIVE 01" time="08:17:04">DIVERGENCE</ChapterMeta>
          <h2 id="turn-title">The<br /><em>Turn</em></h2>
          <p className="chapter-lead">You missed a train. A door closed.<br />The universe did not.</p>
          <p>In another thread, your hand is still reaching for it. Two minutes became two cities, two kitchens, two entirely different ways of laughing.</p>
          <div className="artifact-note"><span>OBSERVATION</span><p>The sculpture separates into twin timelines as you pass.</p></div>
        </div>
        <span className="chapter-index" aria-hidden="true">I</span>
      </section>

      <section id="wild" className="chapter chapter-left" aria-labelledby="wild-title">
        <div className="chapter-card">
          <ChapterMeta code="ARCHIVE 02" time="23:42:19">DEPARTURE</ChapterMeta>
          <h2 id="wild-title">The<br /><em>Wild</em></h2>
          <p className="chapter-lead">Somewhere, you chose the road<br />without a name.</p>
          <p>You learned the weather by smell. You kept your pockets full of seeds and became fluent in leaving. The world grew around the shape of your courage.</p>
          <div className="artifact-note"><span>OBSERVATION</span><p>Every shard finds a place in the bloom. Move your pointer to bend the field.</p></div>
        </div>
        <span className="chapter-index" aria-hidden="true">II</span>
      </section>

      <section id="still" className="chapter chapter-right" aria-labelledby="still-title">
        <div className="chapter-card">
          <ChapterMeta code="ARCHIVE 03" time="06:03:51">CONTINUANCE</ChapterMeta>
          <h2 id="still-title">The<br /><em>Still</em></h2>
          <p className="chapter-lead">Somewhere, you stayed.</p>
          <p>The ordinary days gathered until they became a life. The same blue cup. The light at four. A hand finding yours in the dark. Nothing spectacular. Everything sacred.</p>
          <div className="artifact-note"><span>OBSERVATION</span><p>A suspended tide remembers the gravity of home.</p></div>
        </div>
        <span className="chapter-index" aria-hidden="true">III</span>
      </section>

      <section id="echo" className="echo-section" aria-labelledby="echo-title">
        <div className="echo-panel">
          <p className="eyebrow">A small act of authorship</p>
          <h2 id="echo-title">Give the archive one word<br /><em>your other self kept.</em></h2>
          <p className="echo-instruction">Your choice changes the light, the sculpture, and what follows you out.</p>
          <div className="choice-list" role="group" aria-label="Choose a word">
            {(['wild', 'brave', 'tender'] as EchoChoice[]).map((word, index) => (
              <button
                type="button"
                key={word}
                onClick={() => choose(word, [329.63, 392, 493.88][index])}
                className={choice === word ? 'is-selected' : ''}
                style={{ '--choice-color': echoColors[word] } as React.CSSProperties}
                aria-pressed={choice === word}
              >
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{word}</strong>
                <i><ArrowIcon /></i>
              </button>
            ))}
          </div>
          <p className="choice-result">The archive now remembers you as <strong style={{ color: echoColors[choice] }}>{choice}</strong>.</p>
        </div>
      </section>

      <section id="coda" className={`coda-section ${released ? 'has-released' : ''}`} aria-labelledby="coda-title">
        <div className="coda-copy">
          <p className="eyebrow">Coda / The life that happened</p>
          <h2 id="coda-title">You are not<br />a single story.</h2>
          <p>You are the brief light between every <em>almost</em> and every <em>after</em>. This life is not smaller because the others could exist. It is luminous because only this one did.</p>
          <button className="release-button" type="button" onClick={releaseEcho}>
            <span>{released ? 'Echo released' : 'Release your echo'}</span>
            <i><AsteriskIcon /></i>
          </button>
          <div className="release-message" aria-live="polite">
            <span>ARCHIVE RECEIPT</span>
            <strong>{released ? `ECHO–${choice.toUpperCase()}–∞` : 'WAITING FOR SIGNAL'}</strong>
          </div>
        </div>
        <div className="coda-marquee" aria-hidden="true">
          <span>YOU WERE HERE&nbsp;·&nbsp;YOU ARE HERE&nbsp;·&nbsp;YOU WILL HAVE BEEN HERE&nbsp;·&nbsp;</span>
          <span>YOU WERE HERE&nbsp;·&nbsp;YOU ARE HERE&nbsp;·&nbsp;YOU WILL HAVE BEEN HERE&nbsp;·&nbsp;</span>
        </div>
        <footer>
          <div><span>ALMOST</span><p>The Museum of Unlived Lives</p></div>
          <div><span>BUILT WITH</span><p>React · Three.js · WebGL · Procedural Audio</p></div>
          <div><span>NO MODELS. NO FOOTAGE.</span><p>Every visible artifact is generated in real time.</p></div>
          <div><span>CREATED BY</span><p>Your Name · 2026</p></div>
        </footer>
      </section>
    </main>
  )
}

export default function App() {
  const [bootProgress, setBootProgress] = useState(0)
  const [entered, setEntered] = useState(false)
  const [departing, setDeparting] = useState(false)
  const [choice, setChoice] = useState<EchoChoice>('wild')
  const [released, setReleased] = useState(false)
  const sound = useAmbientSound()
  const chapter = useExperienceScroll(entered)
  const accentStyle = useMemo(() => ({ '--accent': echoColors[choice] } as React.CSSProperties), [choice])

  useEffect(() => {
    document.body.classList.toggle('is-locked', !entered)
    const start = performance.now()
    const duration = 1850
    let frame = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setBootProgress(Math.min(100, eased * 103))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [entered])

  useEffect(() => {
    if (!entered) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'm') sound.toggle()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [entered, sound])

  const enter = (withSound: boolean) => {
    if (departing) return
    if (withSound) sound.start()
    experienceState.started = true
    setDeparting(true)
    window.scrollTo(0, 0)
    window.setTimeout(() => setEntered(true), 900)
  }

  const releaseEcho = () => {
    experienceState.releasePulse = 1
    setReleased(true)
    sound.chime(choice === 'wild' ? 261.63 : choice === 'brave' ? 329.63 : 440)
    window.setTimeout(() => setReleased(false), 5200)
  }

  return (
    <div className={`app ${entered ? 'has-entered' : 'is-loading'}`} style={accentStyle}>
      <a className="skip-link" href="#main-content">Skip to the story</a>
      <SceneBoundary><ArchiveScene choice={choice} /></SceneBoundary>
      {entered && <Header chapter={chapter} soundActive={sound.started && sound.enabled} toggleSound={sound.toggle} />}
      <Story choice={choice} setChoice={setChoice} released={released} releaseEcho={releaseEcho} chime={sound.chime} />
      {!entered && <Loader progress={bootProgress} exiting={departing} onEnter={() => enter(true)} onEnterSilent={() => enter(false)} />}
      <div className="page-progress" aria-hidden="true"><span /></div>
      <div className="screen-grain" aria-hidden="true" />
      <Cursor />
    </div>
  )
}
